import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Sparkles } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Streak Reward — 10-day streak unlocks a free month of AI Mastery, every 90 days.
 * Renders nothing unless the user is eligible (`streak_reward_eligible` from /auth/me).
 */
export default function StreakRewardBanner() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!user?.streak_reward_eligible) return;
    (async () => {
      try {
        const r = await apiClient.get("/streak/reward-status");
        setStatus(r.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[streak-banner] reward-status fetch failed:", e?.message);
        // Silent fail OK — banner just won't render
      }
    })();
  }, [user?.streak_reward_eligible]);

  if (!user?.streak_reward_eligible || !status?.eligible) return null;

  const claim = async () => {
    const tier = "mastery_monthly";
    setBusy(tier);
    try {
      const r = await apiClient.post("/streak/claim-reward", { tier });
      toast.success(`Free month of AI Mastery unlocked. Ends ${new Date(r.data.new_until).toLocaleDateString()}.`);
      await refresh?.();
      setStatus(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim reward");
    } finally {
      setBusy(null);
    }
  };

  return (
    <motion.div
      initial={{ y: -8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="brut p-4 md:p-5 mb-6 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 60%, #FBBF24 100%)",
        borderColor: "rgba(180, 83, 9, 0.35)",
      }}
      data-testid="streak-reward-banner"
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-yellow-400 opacity-30 blur-2xl pointer-events-none" />
      <div className="flex items-start gap-4 flex-wrap relative">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: "#0F1419" }}
        >
          <Trophy size={22} color="#FBBF24" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#92400E]">
            {status.total_claims > 0 ? `Reward #${status.total_claims + 1} unlocked` : "Reward unlocked"}
          </div>
          <div className="font-display text-xl md:text-2xl mt-0.5 leading-tight text-[#0F1419]">
            You hit a {status.streak}-day streak. Claim a free month.
          </div>
          <div className="text-sm text-[#5B3F0E] mt-1 leading-snug">
            We'll add 30 days of AI Mastery. Next reward unlocks {status.cooldown_days} days from claim.
          </div>
        </div>
        <button
          onClick={claim}
          disabled={!!busy}
          className="btn-primary whitespace-nowrap"
          style={{ padding: "0.6rem 1.1rem", fontSize: "0.85rem" }}
          data-testid="streak-reward-claim"
        >
          {busy ? <span className="spinner" /> : <><Sparkles size={13} /> Claim free month</>}
        </button>
      </div>
    </motion.div>
  );
}
